import { useQueryClient, UseMutationResult, useMutation, UseQueryResult, useQuery } from '@tanstack/react-query'
import {
  FilterParcelCustomerListInput,
  ParcelCustomerByAgentInput,
  ParcelCustomerIdentityInput,
  UpdateParcelAddressInput,
  ParcelCustomerListResponse,
  ParcelCustomerAddressesResponse,
  ParcelCustomerParcelsResponse,
  UpdateParcelAddressResponse,
  DeleteParcelAddressResponse,
} from 'services/interface/parcelCustomers.interface'
import {
  getParcelCustomerList,
  getParcelCustomerAddresses,
  getParcelCustomerParcels,
  updateParcelAddress,
  deleteParcelAddress,
} from 'services/parcelCustomersApi'

export const useParcelCustomerList = (
  input: FilterParcelCustomerListInput
): UseQueryResult<ParcelCustomerListResponse, Error> => {
  return useQuery<ParcelCustomerListResponse, Error>({
    queryKey: ['parcelCustomerList', input.search, input.offset, input.limit],
    queryFn: () => getParcelCustomerList(input),
  })
}

export const useParcelCustomerAddresses = (
  input: ParcelCustomerIdentityInput,
  enabled = true
): UseQueryResult<ParcelCustomerAddressesResponse, Error> => {
  return useQuery<ParcelCustomerAddressesResponse, Error>({
    queryKey: ['parcelCustomerAddresses', input.phone, input.name],
    queryFn: () => getParcelCustomerAddresses(input),
    enabled,
  })
}

export const useParcelCustomerParcels = (
  input: ParcelCustomerByAgentInput,
  enabled = true
): UseQueryResult<ParcelCustomerParcelsResponse, Error> => {
  return useQuery<ParcelCustomerParcelsResponse, Error>({
    queryKey: ['parcelCustomerParcels', input.agent_id, input.offset, input.limit],
    queryFn: () => getParcelCustomerParcels(input),
    enabled,
  })
}

export const useUpdateParcelAddress = (
  phone: string,
  name: string
): UseMutationResult<UpdateParcelAddressResponse, Error, { id: string; input: UpdateParcelAddressInput }, unknown> => {
  const queryClient = useQueryClient()
  return useMutation<UpdateParcelAddressResponse, Error, { id: string; input: UpdateParcelAddressInput }, unknown>({
    mutationFn: ({ id, input }) => updateParcelAddress(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parcelCustomerAddresses', phone, name] })
    },
  })
}

export const useDeleteParcelAddress = (
  phone: string,
  name: string
): UseMutationResult<DeleteParcelAddressResponse, Error, string, unknown> => {
  const queryClient = useQueryClient()
  return useMutation<DeleteParcelAddressResponse, Error, string, unknown>({
    mutationFn: (id: string) => deleteParcelAddress(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parcelCustomerAddresses', phone, name] })
      queryClient.invalidateQueries({ queryKey: ['parcelCustomerList'] })
    },
  })
}
